import React, { useCallback, useEffect, useState } from "react";
import { auth } from "../Lib/Firebase";
import { browserLocalPersistence, GoogleAuthProvider, getRedirectResult, MultiFactorResolver, onAuthStateChanged, setPersistence, signInWithPopup, signInWithRedirect, User } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, LockKeyhole, ShieldCheck, Sparkles } from "lucide-react";
import { bootstrapAdminAccess } from "../services/adminAccess";
import { resolveTotpSignIn, totpResolverFromError } from "../services/adminMfa";
import AdminLoginControls from "./security/AdminLoginControls";

const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

const AdminLogin: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
    const navigate = useNavigate();

    const routeAuthenticatedAdmin = useCallback(async (user: User) => {
        const access = await bootstrapAdminAccess();
        if (!access.approved) {
            navigate("/dashboard", { replace: true });
            return false;
        }
        navigate("/admin", { replace: true });
        return true;
    }, [navigate]);

    const beginMfa = (error: any, fallback: string) => {
        try {
            const resolver = totpResolverFromError(error);
            if (resolver) { setMfaResolver(resolver); setErr(null); return; }
        } catch (mfaError: any) { setErr(mfaError?.message || fallback); return; }
        setErr(error?.message || fallback);
    };

    useEffect(() => {
        getRedirectResult(auth)
            .then((credential) => credential ? routeAuthenticatedAdmin(credential.user) : undefined)
            .catch((error: any) => beginMfa(error, "Google sign-in could not be completed."))
            .finally(() => setLoading(false));

        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user?.email) {
                setLoading(false);
                return;
            }
            try {
                await routeAuthenticatedAdmin(user);
            } catch (error: any) {
                setErr(error?.message || "Admin access could not be verified.");
            } finally {
                setLoading(false);
            }
        });
        return () => unsub();
    }, [routeAuthenticatedAdmin]);

    const loginWithGoogle = async () => {
        setErr(null);
        setLoading(true);
        try {
            await setPersistence(auth, browserLocalPersistence);
            const credential = await signInWithPopup(auth, provider);
            await routeAuthenticatedAdmin(credential.user);
        } catch (e: any) {
            const redirectFallbackCodes = [
                "auth/popup-blocked",
                "auth/operation-not-supported-in-this-environment",
                "auth/web-storage-unsupported",
            ];
            if (e?.code === "auth/multi-factor-auth-required") {
                beginMfa(e, "Authenticator verification could not be started.");
            } else if (redirectFallbackCodes.includes(e?.code)) {
                try {
                    await signInWithRedirect(auth, provider);
                    return;
                } catch (redirectError: any) {
                    setErr(redirectError?.message || "Google sign-in could not be started.");
                }
            } else if (e?.code === "auth/popup-closed-by-user") {
                setErr("Google sign-in was cancelled before an account was selected.");
            } else {
                setErr(e?.message ?? "Google sign-in failed");
            }
        } finally {
            setLoading(false);
        }
    };

    const verifyMfa = async (code: string) => {
        if (!mfaResolver) return;
        setLoading(true); setErr(null);
        try {
            const credential = await resolveTotpSignIn(mfaResolver, code);
            setMfaResolver(null);
            await routeAuthenticatedAdmin(credential.user);
        } catch (error: any) {
            setErr(error?.code === "auth/invalid-verification-code" ? "That authenticator code is invalid or expired." : error?.message || "Authenticator verification failed.");
        } finally { setLoading(false); }
    };

    return (
        <main className="relative min-h-screen overflow-hidden bg-[#f7faff] text-slate-900">
            <div className="pointer-events-none absolute -left-32 top-1/3 h-80 w-80 rounded-full bg-cyan-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-blue-400/20 blur-3xl" />

            <div className="relative mx-auto grid min-h-screen w-full max-w-[1500px] lg:grid-cols-[1.05fr_0.95fr]">
                <section className="relative hidden overflow-hidden bg-gradient-to-br from-[#082f84] via-[#1155c5] to-[#21b8e8] p-12 text-white lg:flex lg:flex-col lg:justify-between xl:p-16">
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('/images/svg/Header.svg')", backgroundPosition: "center", backgroundSize: "cover" }} />
                    <div className="absolute -right-24 top-24 h-80 w-80 rounded-full border border-white/20 bg-white/10" />
                    <div className="absolute -bottom-28 -left-16 h-80 w-80 rounded-full border-[40px] border-cyan-200/15" />

                    <div className="relative flex items-center gap-3">
                        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white shadow-lg shadow-blue-950/20">
                            <img src="/images/jpg/mockzo-logo-v3.webp" alt="MockZo logo" className="h-10 w-10" />
                        </span>
                        <div>
                            <p className="text-2xl font-black tracking-tight">MockZo</p>
                            <p className="text-xs font-bold uppercase tracking-[0.28em] text-cyan-100">Build • Learn • Grow</p>
                        </div>
                    </div>

                    <div className="relative max-w-xl">
                        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold backdrop-blur">
                            <Sparkles className="h-4 w-4 text-cyan-200" /> MockZo Operations Centre
                        </div>
                        <h1 className="text-5xl font-black leading-[1.08] tracking-tight xl:text-6xl">Manage every learner journey with confidence.</h1>
                        <p className="mt-6 max-w-lg text-lg leading-8 text-blue-50">Control students, partners, course runs, payments and learning resources from one secure workspace.</p>
                    </div>

                    <div className="relative grid grid-cols-3 gap-3 text-sm">
                        {["Secure access", "Live operations", "MockZo support"].map((item) => (
                            <div key={item} className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                                <CheckCircle2 className="mb-2 h-5 w-5 text-cyan-200" />
                                <span className="font-semibold">{item}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex min-h-screen items-center justify-center px-4 py-8 sm:px-8 lg:px-12">
                    <div className="w-full max-w-lg">
                        <div className="mb-8 flex items-center justify-between lg:hidden">
                            <a href="/" className="flex items-center gap-3">
                                <span className="grid h-11 w-11 place-items-center rounded-xl bg-white shadow-md ring-1 ring-blue-100">
                                    <img src="/images/jpg/mockzo-logo-v3.webp" alt="MockZo logo" className="h-8 w-8" />
                                </span>
                                <span className="text-xl font-black text-[#0b2f75]">MockZo</span>
                            </a>
                            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">Admin Portal</span>
                        </div>

                        <div className="rounded-[2rem] border border-blue-100 bg-white p-6 shadow-2xl shadow-blue-900/10 sm:p-9">
                            <div className="mb-7 flex items-start justify-between gap-4">
                                <div>
                                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-400 text-white shadow-lg shadow-blue-500/20">
                                        <ShieldCheck className="h-6 w-6" />
                                    </div>
                                    <h1 className="text-3xl font-black tracking-tight text-[#0b2f75]">Admin Sign In</h1>
                                    <p className="mt-2 text-sm leading-6 text-slate-500">Use an approved MockZo administrator account to continue.</p>
                                </div>
                                <LockKeyhole className="mt-2 h-6 w-6 text-blue-200" />
                            </div>

                            <AdminLoginControls busy={loading} error={err} mfaRequired={Boolean(mfaResolver)} onGoogle={loginWithGoogle} onVerifyMfa={verifyMfa} />

                            <a href="/" className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-blue-50 hover:text-blue-700">
                                <ArrowLeft className="h-4 w-4" /> Not an admin? Return to MockZo home
                            </a>

                        </div>

                        <p className="mt-6 text-center text-xs leading-5 text-slate-400">Secure access powered by Firebase Authentication<br />MockZo © {new Date().getFullYear()}</p>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default AdminLogin;
